import React, { useState, useCallback } from 'react'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import ItineraryMap from 'components/Cruises/CruiseOfferPage/content/Itinerary/ItineraryMap'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import useCruiseUniqueItineraryList from 'hooks/Cruise/useCruiseUniqueItineraryList'
import offerPageURL from 'lib/offer/offerPageURL'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'

const DayText = styled(BodyText)`
  width: ${rem(55)};
`

const Itinerary = styled.div`
  order: 4;
  padding: ${rem(24)} ${rem(24)} 0 ${rem(24)};
`

const Footer = styled(ModalFooter)`
  box-shadow: ${props => props.theme.shadow.top.small};
`

interface Props {
  itinerary?: Array<App.CruiseItineraryItem>;
  offerTitle: string;
  flashOffer?: App.CruiseOffer | App.Offer | App.OfferSummary;
  itineraryFlash?: string;
  isFlashOffer?: boolean;
  offer?: App.CruiseOffer | App.Offer | App.OfferSummary;
}

function CruiseModalItineraryDetails({
  itinerary,
  offerTitle,
  itineraryFlash,
  isFlashOffer,
  offer,
}: Props) {
  const dispatch = useAppDispatch()
  const [_, setMap] = useState<google.maps.Map | null>(null)
  const [isOpen, setIsOpen] = useToggle(true)

  const uniqueItineraryList = useCruiseUniqueItineraryList({
    itinerary: itinerary!,
    isFlashOffer,
  })

  const closeModal = useCallback(() => {
    setIsOpen()
  }, [setIsOpen])

  const onClickModal = useCallback(() => {
    const url = offerPageURL(offer)
    closeModal()
    dispatch(pushWithRegion(url))
  }, [closeModal, dispatch, offer])

  return <ModalBase height="auto" isOpen={isOpen} onClose={closeModal}>
    <ModalHeader
      onCloseButtonClick={closeModal}
      title={`Itinerary for ${offerTitle}`}/>
    <ModalBody>

      {!isFlashOffer && <ItineraryMap
        isModal
        itinerary={itinerary!}
        onMapLoaded={setMap}
        onSelectPort={() => {}} portNameSelected={null}/>}

      <Itinerary>
        <Group direction="vertical" gap={8}>
          {!isFlashOffer && uniqueItineraryList.map((itinerary) => <Group key={itinerary.itineraryKey} direction="horizontal" gap={8}>
            <DayText variant="medium">
              Day {itinerary.item.startDay}
            </DayText>
            <Group direction="horizontal" gap={4}>
              {(itinerary.isStart || itinerary.isEnd || itinerary.addPortCircle) && <BodyText variant="medium" weight="bold">
                {itinerary.isStart && 'Start: '}
                {itinerary.isEnd && 'End: '}
                {itinerary.addPortCircle && !itinerary.isStart && !itinerary.isEnd && `Port ${itinerary.portNumber}: `}
              </BodyText>}

              <BodyText variant="medium" format="titlecase">
                {!!itinerary.item.title && itinerary.item.title.toLowerCase()}
                {!itinerary.item.title && 'At Sea'}
              </BodyText>
            </Group>
          </Group>)}
        </Group>

        {isFlashOffer && itineraryFlash &&
          <MarkdownRender type="compact" fontSize="large" content={itineraryFlash} />}
      </Itinerary>
    </ModalBody>
    <Footer
      primaryActionProps={{
        children: 'View offer',
        onClick: onClickModal,
      }}
    />
  </ModalBase>
}

export default CruiseModalItineraryDetails
